import React, { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { motion } from 'framer-motion';
import { useWeb3 } from '../../context/Web3Context';
import DashboardLoader from '../Loading/DashboardLoader';
import { AnimVal, AnimNum, MCard, PageWrap, FlipClock } from '../UI';
import { formatUSDT } from '../../utils/helpers';

export default function TeamPage() {
  const { contracts, userId } = useWeb3();
  const [loading, setLoading] = useState(true);
  const [d, setD] = useState(null);
  const [tree, setTree] = useState(null);
  const [treeUser, setTreeUser] = useState(null);
  const [treePath, setTreePath] = useState([]);
  const [daily, setDaily] = useState(null);
  const [lvl, setLvl] = useState(1);
  const [lvlUsers, setLvlUsers] = useState([]);
  const [binaryResetTime, setBinaryResetTime] = useState(0);
  const [todayBiz, setTodayBiz] = useState({ left: ethers.BigNumber.from(0), right: ethers.BigNumber.from(0) });
  const [treeNodeDetails, setTreeNodeDetails] = useState({});

  useEffect(() => {
    if (!contracts.storage || !userId) return;
    (async () => {
      try {
        const [di, bi, bd] = await Promise.all([
          contracts.storage.getDirectInfo(userId), contracts.storage.getBinaryInfo(userId), contracts.storage.getDailyBinaryStatus(userId)
        ]);
        setD({ dr: di[0], qd: di[1], tc: di[2], db: di[3], lb: bi[2], rb: bi[3], lbt: bi[4], rbt: bi[5], lm: bi[6], rm: bi[7] });
        setDaily({ cap: bd[0], matched: bd[1], rem: bd[2] });
        setBinaryResetTime(bd[3].toNumber());
        setTreeUser(userId); setTreePath([userId]);
        try { const tb = await contracts.storage.getTodayBusiness(userId); setTodayBiz({ left: tb[0], right: tb[1] }); } catch {}
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [contracts.storage, userId]);

  const loadTree = useCallback(async (uid) => {
    if (!contracts.storage) return;
    try {
      const tr = await contracts.storage.getBinaryTree(uid);
      const nodes = { l:tr[0]?ethers.BigNumber.from(tr[0]).toNumber():0, r:tr[1]?ethers.BigNumber.from(tr[1]).toNumber():0, ll:tr[2]?ethers.BigNumber.from(tr[2]).toNumber():0, lr:tr[3]?ethers.BigNumber.from(tr[3]).toNumber():0, rl:tr[4]?ethers.BigNumber.from(tr[4]).toNumber():0, rr:tr[5]?ethers.BigNumber.from(tr[5]).toNumber():0 };
      setTree(nodes);
      const details = {};
      for (const nid of [uid, nodes.l, nodes.r, nodes.ll, nodes.lr, nodes.rl, nodes.rr].filter(id => id > 0)) {
        try {
          const [bi, ui] = await Promise.all([
            contracts.storage.getBinaryInfo(nid),
            contracts.storage.getUserInfo(nid)
          ]);
          details[nid] = { lb: bi[2], rb: bi[3], invested: ui[3] };
        } catch {}
      }
      setTreeNodeDetails(details);
    } catch {}
  }, [contracts.storage]);

  useEffect(() => { if (treeUser) loadTree(treeUser); }, [treeUser, loadTree]);
  const navigateTree = (uid) => { if (uid <= 0) return; setTreeUser(uid); setTreePath(p => [...p, uid]); };
  const goBack = () => { if (treePath.length <= 1) return; const np = [...treePath]; np.pop(); setTreePath(np); setTreeUser(np[np.length-1]); };
  const goHome = () => { setTreeUser(userId); setTreePath([userId]); };

  useEffect(() => {
    if (!contracts.storage || !userId) return;
    contracts.storage.getLevelUsers(userId, lvl).then(u => setLvlUsers(u.map(id => id.toNumber()).filter(id => id > 0))).catch(() => setLvlUsers([]));
  }, [contracts.storage, userId, lvl]);

  if (loading) return <DashboardLoader />;
  if (!d) return null;
  const isMe = (id) => id === userId;
  const nd = (id) => treeNodeDetails[id] || null;

  const N = ({ id, label }) => {
    const n = id || 0; const cls = n > 0 ? (isMe(n) ? 'me' : 'on') : 'empty'; const det = nd(n);
    return (
      <motion.div className={`tnode ${cls}`} onClick={() => n > 0 && !isMe(n) && navigateTree(n)}
        whileHover={n > 0 && !isMe(n) ? { scale: 1.05 } : {}} whileTap={n > 0 && !isMe(n) ? { scale: 0.9 } : {}}
        initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', bounce: 0.4 }} style={{ minWidth: 68 }}>
        <div className="tnode-label">{label}</div><div style={{ fontWeight: 700 }}>{n > 0 ? `#${n}` : '\u2014'}</div>
        {det && n > 0 && <div style={{ fontSize:'0.42rem', color:'var(--text3)', marginTop:3, lineHeight:1.4 }}>
          <div>💰 <span style={{ color:'var(--gold)', fontWeight: 700 }}>${formatUSDT(det.invested)}</span></div>
          <div>L: <span style={{ color:'var(--cyan)' }}>${formatUSDT(det.lb)}</span></div>
          <div>R: <span style={{ color:'var(--purple)' }}>${formatUSDT(det.rb)}</span></div>
        </div>}
      </motion.div>
    );
  };

  return (<PageWrap>
    <div className="stitle">Binary Stats</div>
    <div className="g4">
      {[{l:'Directs',v:d.dr},{l:'Qualified Directs',v:d.qd,tip:true},{l:'Team',v:d.tc},{l:'Direct Biz',v:d.db,f:1},{l:'Left Business',v:d.lbt,f:1},{l:'Right Business',v:d.rbt,f:1},{l:'Left Members',v:d.lm},{l:'Right Members',v:d.rm},{l:'CF Left',v:d.lb,f:1},{l:'CF Right',v:d.rb,f:1}].map((s,i)=>(
        <MCard key={i} delay={i*0.04} style={{ padding:'12px 6px',textAlign:'center',margin:0 }}>
          <div style={{fontSize:'0.88rem',fontWeight:700,color:'var(--cyan)'}}>{s.f?<AnimVal value={s.v}/>:<AnimNum value={s.v}/>}</div>
          <div className="label" style={{fontSize:'0.5rem'}}>{s.l}</div>
          {s.tip && <div style={{fontSize:'0.4rem',color:'var(--text3)',marginTop:2}}>($100+ invested)</div>}
        </MCard>
      ))}
    </div>

    <div className="stitle">Today's Business</div>
    <MCard delay={0.42}>
      <div className="g2">
        <div className="fcc" style={{ padding:'14px 10px' }}><AnimVal value={todayBiz.left} className="val val-sm val-cyan" /><div className="label">Left Today</div></div>
        <div className="fcc" style={{ padding:'14px 10px' }}><AnimVal value={todayBiz.right} className="val val-sm val-purple" /><div className="label">Right Today</div></div>
      </div>
      {binaryResetTime > 0 && <div style={{ textAlign:'center', padding:'10px', background:'rgba(123,97,255,0.05)', borderRadius:10, border:'1px solid rgba(123,97,255,0.1)', marginTop:8 }}>
        <div style={{ fontSize:'0.5rem', color:'var(--text3)', textTransform:'uppercase', letterSpacing:'1.5px', marginBottom:6 }}>Resets In</div>
        <FlipClock targetTime={binaryResetTime} />
      </div>}
    </MCard>

    {daily && (<><div className="stitle">Daily Binary Status</div>
      <MCard delay={0.45}>
        <div className="g3" style={{ marginBottom:14 }}>
          {[{l:'Cap',v:daily.cap,c:'val-gold'},{l:'Matched',v:daily.matched,c:'val-cyan'},{l:'Remaining',v:daily.rem,c:'val-green'}].map((x,i)=>(<div key={i} className="fcc"><AnimVal value={x.v} className={`val val-sm ${x.c}`}/><div className="label">{x.l}</div></div>))}
        </div>
      </MCard>
    </>)}

    <div className="stitle">Binary Tree</div>
    <MCard delay={0.5} style={{ overflowX:'auto' }}>
      <div style={{ display:'flex',alignItems:'center',gap:6,marginBottom:14,flexWrap:'wrap' }}>
        {treePath.length > 1 && <div style={{ display:'flex',gap:6 }}>
          <motion.button className="btn btn-ghost" style={{ padding:'4px 10px',fontSize:'0.68rem',borderRadius:8 }} onClick={goHome} whileTap={{ scale:0.9 }}>🏠 You</motion.button>
          <motion.button className="btn btn-ghost" style={{ padding:'4px 10px',fontSize:'0.68rem',borderRadius:8 }} onClick={goBack} whileTap={{ scale:0.9 }}>← Back</motion.button>
        </div>}
        <div style={{ display:'flex',alignItems:'center',gap:4,flexWrap:'wrap' }}>
          {treePath.map((id,i)=>(<span key={i} style={{ fontSize:'0.62rem',color:isMe(id)?'var(--cyan)':'var(--purple)',fontFamily:'var(--font-m)' }}>{i>0&&<span style={{ color:'var(--text3)',margin:'0 4px' }}>&rarr;</span>}#{id}</span>))}
        </div>
      </div>
      <div style={{ display:'flex',gap:12,marginBottom:14,fontSize:'0.6rem' }}>
        <span style={{ color:'var(--cyan)' }}>● You</span><span style={{ color:'var(--purple)' }}>● Downline</span><span style={{ color:'var(--text3)' }}>● Empty</span>
      </div>
      <div style={{ display:'flex',flexDirection:'column',alignItems:'center',gap:14,minWidth:300,padding:'8px 0' }}>
        <N id={treeUser} label={isMe(treeUser)?'YOU':`#${treeUser}`} />
        <svg width="200" height="25" style={{ overflow:'visible' }}><line x1="100" y1="0" x2="50" y2="25" className="tree-line"/><line x1="100" y1="0" x2="150" y2="25" className="tree-line"/></svg>
        <div style={{ display:'flex',gap:50,justifyContent:'center' }}><N id={tree?.l} label="Left"/><N id={tree?.r} label="Right"/></div>
        <svg width="280" height="25" style={{ overflow:'visible' }}><line x1="65" y1="0" x2="35" y2="25" className="tree-line"/><line x1="65" y1="0" x2="95" y2="25" className="tree-line"/><line x1="215" y1="0" x2="185" y2="25" className="tree-line"/><line x1="215" y1="0" x2="245" y2="25" className="tree-line"/></svg>
        <div style={{ display:'flex',gap:16,justifyContent:'center',flexWrap:'wrap' }}><N id={tree?.ll} label="LL"/><N id={tree?.lr} label="LR"/><N id={tree?.rl} label="RL"/><N id={tree?.rr} label="RR"/></div>
      </div>
    </MCard>

    <div className="stitle">Level Users</div>
    <MCard delay={0.6}>
      <div style={{ marginBottom:14 }}>
        <select value={lvl} onChange={e => setLvl(parseInt(e.target.value))} style={{ width:'100%',padding:'12px 16px',background:'rgba(20,30,65,0.6)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:12,color:'#fff',fontSize:'0.85rem',fontFamily:'var(--font-h)',appearance:'none',cursor:'pointer',backgroundImage:'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%2300F5FF\' stroke-width=\'2\'%3E%3Cpolyline points=\'6 9 12 15 18 9\'/%3E%3C/svg%3E")',backgroundRepeat:'no-repeat',backgroundPosition:'right 14px center' }}>
          {Array.from({length:10},(_,i)=>i+1).map(l=>(<option key={l} value={l} style={{ background:'#0a1128' }}>Level {l}</option>))}
        </select>
        <div style={{ fontSize:'0.6rem',color:'var(--text3)',marginTop:6 }}>{lvl === 1 ? 'Level 1: Leadership income only (5-25% based on qualifying directs)' : `Need ${lvl} qualifying directs for Level ${lvl} income (5%)`}</div>
      </div>
      {lvlUsers.length === 0 ? <p style={{ color:'var(--text3)',textAlign:'center',padding:12,fontSize:'0.82rem' }}>No users at Level {lvl}</p> :
        <><div style={{ fontSize:'0.7rem',color:'var(--text2)',marginBottom:8 }}>{lvlUsers.length} user{lvlUsers.length>1?'s':''} at Level {lvl}</div>
        <div style={{ display:'flex',flexWrap:'wrap',gap:6 }}>{lvlUsers.map(id=><motion.span key={id} className="badge badge-c" initial={{scale:0}} animate={{scale:1}}>#{id}</motion.span>)}</div></>}
    </MCard>
  </PageWrap>);
}
